/**
 * High-Performance Cache System
 * Provides LRU (Least Recently Used) caching with TTL support
 */

interface CacheEntry<T> {
  value: T;
  timestamp: number;
  accessCount: number;
}

interface CacheOptions {
  maxSize?: number;
  ttl?: number; // Time to live in milliseconds
}

export class Cache<T> {
  private cache: Map<string, CacheEntry<T>>;
  private readonly maxSize: number;
  private readonly ttl: number;
  private sessionLocked = false;

  constructor(options: CacheOptions = {}) {
    this.cache = new Map();
    this.maxSize = options.maxSize || 100;
    this.ttl = options.ttl || 5 * 60 * 1000; // Default: 5 minutes
  }

  lockSession(): void { this.sessionLocked = true; }
  unlockSession(): void { this.sessionLocked = false; }

  /**
   * Gets a value from the cache
   */
  get(key: string): T | null {
    const entry = this.cache.get(key);

    if (!entry) {
      return null;
    }

    // Check if entry has expired
    if (!this.sessionLocked && Date.now() - entry.timestamp > this.ttl) {
      this.cache.delete(key);
      return null;
    }

    // Update access count for LRU
    entry.accessCount++;
    entry.timestamp = Date.now();

    return entry.value;
  }

  /**
   * Sets a value in the cache
   */
  set(key: string, value: T): void {
    // If cache is full, remove least recently used item
    if (this.cache.size >= this.maxSize && !this.cache.has(key)) {
      this.evictLRU();
    }

    this.cache.set(key, {
      value,
      timestamp: Date.now(),
      accessCount: 1,
    });
  }

  /**
   * Checks if a key exists and is not expired
   */
  has(key: string): boolean {
    const entry = this.cache.get(key);

    if (!entry) {
      return false;
    }

    // Check if entry has expired
    if (!this.sessionLocked && Date.now() - entry.timestamp > this.ttl) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }

  /**
   * Removes a specific key from the cache
   */
  delete(key: string): boolean {
    return this.cache.delete(key);
  }

  /**
   * Clears all cache entries
   */
  clear(): void {
    this.cache.clear();
  }

  /**
   * Gets cache statistics
   */
  getStats(): { size: number; maxSize: number; ttl: number } {
    return {
      size: this.cache.size,
      maxSize: this.maxSize,
      ttl: this.ttl,
    };
  }

  /**
   * Evicts the least recently used entry
   */
  private evictLRU(): void {
    let lruKey: string | null = null;
    let oldestTime = Date.now();
    let lowestAccessCount = Infinity;

    for (const [key, entry] of this.cache.entries()) {
      // Prioritize by access count first, then by timestamp
      if (
        entry.accessCount < lowestAccessCount ||
        (entry.accessCount === lowestAccessCount && entry.timestamp < oldestTime)
      ) {
        lruKey = key;
        oldestTime = entry.timestamp;
        lowestAccessCount = entry.accessCount;
      }
    }

    if (lruKey) {
      this.cache.delete(lruKey);
    }
  }

  /**
   * Gets or sets a value using a factory function
   */
  async getOrSet(key: string, factory: () => Promise<T>): Promise<T> {
    const cached = this.get(key);

    if (cached !== null) {
      return cached;
    }

    const value = await factory();
    this.set(key, value);
    return value;
  }

  /**
   * Removes all expired entries
   */
  cleanExpired(): number {
    let removedCount = 0;
    const now = Date.now();

    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp > this.ttl) {
        this.cache.delete(key);
        removedCount++;
      }
    }

    return removedCount;
  }
}

// Create a singleton instance for Pokemon data
export const pokemonCache = new Cache<any>({
  maxSize: 200,
  ttl: 30 * 60 * 1000, // 30 minutes for Pokemon data
});
